import Service from '@ember/service';
import config from 'cio/config/environment';
import { requiredFacebookAccessTokenScopes as requiredFacebookAccessTokenScopesForLeadAds } from 'cio/services/facebook-lead-ads';

export type FacebookConnectLoginOptions = {
  feature?: 'ad_audiences' | 'lead_ads';
};

export default class FacebookConnectService extends Service {
  private locale = 'en_US';

  // https://developers.facebook.com/docs/javascript/reference/FB.init/v12.0
  private readonly initOptions = {
    appId: config.oAuth.facebookConnect.appId,
    version: 'v2.7',
    cookie: true,
    localStorage: true,
    status: true,
    xfbml: false,
  };

  async login(options: FacebookConnectLoginOptions): Promise<fb.StatusResponse['authResponse']> {
    let loginOptions: fb.LoginOptions = { return_scopes: false };

    // We use "facebook-connect" auth for 2 Facebook-related features: Ad Audiences and Lead Ads
    // They both require different permission scopes
    // This override makes sure it's correctly set for each "oauth.open(...)" call:
    if (options?.feature === 'ad_audiences') {
      loginOptions.scope = 'ads_management';
    } else if (options?.feature === 'lead_ads') {
      loginOptions.scope = requiredFacebookAccessTokenScopesForLeadAds.join(',');
    }

    await this.loadFbConnect();

    return new Promise((resolve, reject) => {
      window.FB.login((response) => {
        if (response.status === 'authorization_expired' || response.status === 'not_authorized') {
          return reject(new Error(`Invalid login status: ${response.status}`));
        }

        return resolve(response.authResponse);
      }, loginOptions);
    });
  }

  private async loadFbConnect(): Promise<boolean> {
    if (window.FB) {
      return true;
    }

    return new Promise((resolve) => {
      window.fbAsyncInit = () => {
        window.FB.init(this.initOptions);
        resolve(true);
      };

      this.loadScript();
    });
  }

  private loadScript() {
    let url = `https://connect.facebook.net/${this.locale}/sdk.js`;
    let script = document.createElement('script');

    script.setAttribute('src', url);
    script.setAttribute('async', '');
    script.setAttribute('defer', '');
    /* eslint-disable-next-line spellcheck/spell-checker */
    script.setAttribute('crossorigin', 'anonymous');
    script.setAttribute('id', 'fb-connect-script');

    window.document.body.appendChild(script);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'facebook-connect': FacebookConnectService;
  }
}
